import { updateSessionCounterEndpoint } from '@cp-mx/common';
import { TFunction } from 'i18next';
import React, { useCallback, useMemo } from 'react';
import { CpDataApi } from '../../cp-xhr';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { useAnalyticsActionTracker } from '../../analytics';
import {
    NavigationBar as NavigationBarShared,
    SimpleNavItemProps,
    SiteNavItemPropsList,
    SiteNavProps,
    useOidcAuthentication,
} from '@cp-shared-19/frontend-ui';

import {
    cookiePolicyPath,
    privacyPolicyPath,
    dashboardPagePath,
    faqPagePath,
    landingPagePath,
    legalNoticePath,
    myProfilePagePath,
    notAuthorizedPagePath,
    notFoundPagePath,
    registrationPagePath,
    openRequestPath,
} from './paths';
import { useLogin } from '../../auth/useLogin';
import { useAuthorization } from '../../auth';
import { useNotificationCenter } from '../notification-center/useNotificationCenter';
import { NotificationCenter } from '../notification-center/NotificationCenterComponent';
import { removeInvoiceDeliveryNotificationsForSession } from '../invoice-deliveries-notification/InvoiceDeliveryNotificationsSlice';

const pagesWithoutNavbar = [registrationPagePath()];

const pagesWithoutStaticHeader = [
    landingPagePath(),
    faqPagePath(),
    cookiePolicyPath(),
    privacyPolicyPath(),
    legalNoticePath(),
    notAuthorizedPagePath(),
    notFoundPagePath(),
    registrationPagePath(),
];

function publicNavigationLinks(t: TFunction, sessionButton: SimpleNavItemProps): SiteNavItemPropsList {
    return [
        {
            href: landingPagePath(),
            label: t('navigation.home'),
            isReversed: true,
            url: landingPagePath(),
        },
        sessionButton,
    ];
}
function privateNavigationLinks(t: TFunction, sessionButton: SimpleNavItemProps): SiteNavItemPropsList {
    return [
        {
            href: dashboardPagePath(),
            label: t('navigation.dashboard'),
            isReversed: true,
            url: dashboardPagePath(),
        },
        {
            href: myProfilePagePath(),
            label: t('navigation.profile'),
            isReversed: true,
            url: myProfilePagePath(),
        },
        {
            href: faqPagePath(),
            label: t('navigation.faq'),
            isReversed: true,
            url: faqPagePath(),
        },
        {
            href: openRequestPath(),
            label: t('navigation.openRequest'),
            isReversed: true,
            url: openRequestPath(),
        },
        sessionButton,
    ];
}

export const NavigationBar: React.FC = () => {
    const { t } = useTranslation('navigation');
    const history = useHistory();
    const location = useLocation();

    useNotificationCenter();

    const { isAuthenticated, logout } = useOidcAuthentication();
    const { isAuthorized } = useAuthorization();
    const { onAction: onLogoutAction } = useAnalyticsActionTracker('logout');
    const { onAction: onLoginAction } = useAnalyticsActionTracker('login');

    const login = useLogin();
    const logoutFn = useCallback(
        () => logout({ post_logout_redirect_uri: `${window.location.origin}${landingPagePath()}` }),
        [logout],
    );
    const logoutFnAction = async () => {
        await CpDataApi.patch(updateSessionCounterEndpoint());
        onLogoutAction();
        logoutFn();
    };
    const loginFnAction = () => {
        removeInvoiceDeliveryNotificationsForSession();
        onLoginAction();
        login();
    };
    const sessionButton: SimpleNavItemProps = {
        href: '#',
        label: isAuthenticated ? t('navigation.logout') : t('navigation.login'),
        onClick: isAuthenticated ? logoutFnAction : loginFnAction,
    };

    const navigationItems = useMemo(
        () =>
            (isAuthenticated ? privateNavigationLinks(t, sessionButton) : publicNavigationLinks(t, sessionButton)).map(
                navItem => ({
                    ...{ isActive: location.pathname === navItem.href },
                    ...navItem,
                }),
            ),
        [isAuthenticated, isAuthorized, sessionButton, location.pathname, t],
    );

    const currentPathName = location.pathname;

    const hidden = pagesWithoutNavbar.includes(currentPathName);
    const withStaticHeader = !pagesWithoutStaticHeader.includes(currentPathName);

    const navigation: SiteNavProps = {
        navigationItems,
    };
    const onLogoClick = useCallback(
        () =>
            isAuthenticated && isAuthorized
                ? (() => history.push(dashboardPagePath()))()
                : (() => history.push(landingPagePath()))(),
        [isAuthenticated, isAuthorized, history],
    );

    const notificationsCenter = isAuthenticated && <NotificationCenter />;

    return (
        <NavigationBarShared
            onLogoClick={onLogoClick}
            logoAltText={t(`navigation.${isAuthenticated ? 'dashboard' : 'home'}`)}
            navigation={navigation}
            notificationsCenter={notificationsCenter}
            withStaticHeader={withStaticHeader}
            hidden={hidden}
        />
    );
};
